
import { defineComponent, ref, onMounted, computed, reactive, watch } from "vue";
import { useStore } from "vuex";
import {
  POST_KEGIATAN_KEMITRAAN,
  PUT_KEGIATAN_KEMITRAAN,
} from "@/store/kegiatanKemitraan.module";
import { GET_KEMITRAAN } from "@/store/kemitraan.module";
import { showError } from "@/utils/swal";
import AppSelect from "@/components/input/AppSelect.vue";

interface KemitraanOption {
  id?: number | string;
  name?: string;
}

interface ApiError {
  response?: {
    data?: {
      message?: string;
    };
  };
}

type KegiatanKemitraanFormValue = string | number | File | null | undefined;

const toInputDate = (value: unknown) => {
  if (!value) return "";
  const date = new Date(String(value));
  if (Number.isNaN(date.getTime())) return String(value);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

export default defineComponent({
  components: {
    AppSelect,
  },
  props: {
    title: {
      type: String,
      required: true,
    },
    id: {
      type: String,
      required: false,
    },
    data: {
      type: Object,
      required: false,
      default: () => ({}),
    },
  },
  setup(props, { emit }) {
    const store = useStore();
    const modalContent = ref(null);
    const isLoading = ref(false);
    const imageFile = ref<File | null>(null);
    const imagePreviewLocal = ref("");

    const statusOptions = ref([
      { value: "planned", label: "Direncanakan" },
      { value: "ongoing", label: "Berlangsung" },
      { value: "completed", label: "Selesai" },
      { value: "cancelled", label: "Dibatalkan" },
    ]);

    const kemitraanOptions = computed(() => {
      const list = (store.getters.kemitraan || []) as KemitraanOption[];
      return list
        .filter((k) => k?.id && k?.name)
        .map((k) => ({
          label: k.name,
          value: Number(k.id),
        }));
    });
    const kemitraanSelectOptions = computed(() => [
      { value: "", label: "Pilih mitra", disabled: true },
      ...kemitraanOptions.value,
    ]);

    const closeModal = () => {
      isLoading.value = false;
      emit("close");
    };

    const formData = reactive({
      id: "",
      data: {
        ...props.data,
        status: props.data?.status || "planned",
        kemitraanId: props.data?.kemitraanId ? Number(props.data.kemitraanId) : "",
        startDate: toInputDate(props.data?.startDate),
        endDate: toInputDate(props.data?.endDate),
      } as Record<string, unknown>,
    });

    const imagePreview = computed(() => {
      if (imagePreviewLocal.value) return imagePreviewLocal.value;
      return typeof formData.data.image === "string" ? formData.data.image : "";
    });

    const onImageChange = (event: Event) => {
      const input = event.target as HTMLInputElement;
      const file = input.files?.[0] || null;
      imageFile.value = file;
      if (!file) {
        imagePreviewLocal.value = "";
        return;
      }

      const reader = new FileReader();
      reader.onload = () => {
        imagePreviewLocal.value = (reader.result as string) || "";
      };
      reader.readAsDataURL(file);
      formData.data.image = file;
    };

    const buildPayload = () => {
      if (!imageFile.value) return formData.data;

      const payload = new FormData();
      Object.entries(formData.data).forEach(([key, value]) => {
        if (value === undefined || value === null) return;
        if (key === "image") return;
        payload.append(key, String(value as KegiatanKemitraanFormValue));
      });
      payload.append("image", imageFile.value);
      return payload;
    };

    const handleSubmit = async () => {
      isLoading.value = true;
      try {
        if (!Number.isInteger(Number(formData.data.kemitraanId)) || Number(formData.data.kemitraanId) <= 0) {
          throw new Error("Mitra wajib dipilih dari daftar yang tersedia");
        }
        formData.data.kemitraanId = Number(formData.data.kemitraanId);
        const payload = buildPayload();
        if (props?.id) {
          formData.id = props?.id;
          await store.dispatch(PUT_KEGIATAN_KEMITRAAN, { id: formData.id, data: payload });
        } else {
          await store.dispatch(POST_KEGIATAN_KEMITRAAN, { data: payload });
        }
        closeModal();
      } catch (error: unknown) {
        const apiError = error as ApiError;
        showError("Gagal", apiError?.response?.data?.message || (error instanceof Error ? error.message : "Gagal menyimpan kegiatan kemitraan"));
        isLoading.value = false;
      }
    };

    onMounted(async () => {
      try {
        await store.dispatch(GET_KEMITRAAN, { data: { limit: 100 } });
      } catch (err) {
        showError("Gagal", err instanceof Error ? err.message : "Gagal memuat daftar kemitraan");
      }
    });

    watch(kemitraanOptions, (options) => {
      if (!formData.data.kemitraanId && options.length) {
        formData.data.kemitraanId = options[0].value;
      }
    }, { immediate: true });

    return {
      modalContent,
      formData,
      imagePreview,
      closeModal,
      handleSubmit,
      buildPayload,
      onImageChange,
      isLoading,
      statusOptions,
      kemitraanOptions,
      kemitraanSelectOptions,
    };
  },
});
